import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const CustomOrder = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    hatType: '',
    material: '',
    size: '',
    color: '',
    details: '',
    budget: '',
    timeline: '',
    referenceImages: null
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false
  });
  
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'referenceImages' && files) {
      setFormData(prev => ({
        ...prev,
        [name]: files
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare the data for submission
      const customOrderData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        hatType: formData.hatType,
        material: formData.material,
        size: formData.size,
        color: formData.color,
        details: formData.details,
        budget: formData.budget,
        timeline: formData.timeline,
        referenceImages: [] // We'll handle file uploads separately in the future
      };
      
      // API base URL - use production URL in development and relative path in production
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? '/api'
        : 'https://kalahats.com/api';
      
      // Send the data to the API
      const response = await fetch(`${API_BASE_URL}/custom-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customOrderData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit custom order');
      }
      
      // Set success status
      setFormStatus({
        submitted: true,
        error: false
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        hatType: '',
        material: '',
        size: '',
        color: '',
        details: '',
        budget: '',
        timeline: '',
        referenceImages: null
      });
      
    } catch (error) {
      console.error('Error submitting custom order:', error);
      
      // Set error status
      setFormStatus({
        submitted: false,
        error: true,
        errorMessage: error.message
      });
    }
  };
  
  const hatTypes = ['Sun Hat', 'Beach Hat', 'Fedora', 'Panama Hat', 'Bucket Hat', 'Wide Brim Hat', 'Other'];
  const materials = ['Straw', 'Woven Palm Leaf', 'Fabric', 'Mixed Materials', 'Other'];
  const timelines = ['1-2 weeks', '3-4 weeks', '1-2 months', 'Flexible'];
  
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-black py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 text-center">
            Custom Hat Orders
          </h1>
          <p className="text-light max-w-3xl mx-auto text-center text-lg">
            Looking for something unique? Our artisans can create a custom hat tailored to your preferences and specifications.
          </p>
        </div>
      </div>
      
      {/* Process Section */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold text-primary mb-8 text-center">
            Our Custom Order Process
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">1</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Consultation</h3>
              <p className="text-gray-400">
                Fill out our custom order form with your preferences and requirements.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">2</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Design</h3>
              <p className="text-gray-400">
                Our designers will create a concept based on your specifications.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">3</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Crafting</h3>
              <p className="text-gray-400">
                Our skilled artisans handcraft your custom hat with meticulous attention to detail.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">4</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Delivery</h3>
              <p className="text-gray-400">
                Your unique, handcrafted hat is carefully packaged and shipped to your doorstep.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Custom Order Form */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-secondary p-8 rounded-lg border border-gray-800">
            <h2 className="font-serif text-2xl font-bold text-primary mb-6">
              Custom Order Request
            </h2>
            
            {formStatus.submitted ? (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                <p className="font-medium">Thank you for your custom order request!</p>
                <p>We'll review your requirements and get back to you within 48 hours to discuss the details and provide a quote.</p>
              </div>
            ) : formStatus.error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>There was an error submitting your request: {formStatus.errorMessage || 'Please try again later.'}</p>
              </div>
            ) : null}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="font-medium text-lg border-b border-gray-700 pb-2 mb-4 text-light">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-300 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-gray-300 mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              
              <h3 className="font-medium text-lg border-b border-gray-700 pb-2 mb-4 pt-4 text-light">Hat Specifications</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="hatType" className="block text-gray-300 mb-2">
                    Hat Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="hatType"
                    name="hatType"
                    value={formData.hatType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">Select Hat Type</option>
                    {hatTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="material" className="block text-gray-300 mb-2">
                    Preferred Material <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="material"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">Select Material</option>
                    {materials.map(material => (
                      <option key={material} value={material}>{material}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="size" className="block text-gray-300 mb-2">
                    Hat Size <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="size"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    required
                    placeholder="e.g., S, M, L, XL or head circumference in cm/inches"
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                
                <div>
                  <label htmlFor="color" className="block text-gray-300 mb-2">
                    Color Preferences
                  </label>
                  <input
                    type="text"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., Natural, Black, Blue, etc."
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="details" className="block text-gray-300 mb-2">
                  Design Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Please describe your custom hat in detail. Include any specific design elements, patterns, embellishments, or other features you'd like."
                  className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                ></textarea>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="budget" className="block text-gray-300 mb-2">
                    Budget Range (IDR)
                  </label>
                  <input
                    type="text"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="e.g., Rp 1.500.000 - Rp 3.000.000"
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                
                <div>
                  <label htmlFor="timeline" className="block text-gray-300 mb-2">
                    Preferred Timeline <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">Select Timeline</option>
                    {timelines.map(timeline => (
                      <option key={timeline} value={timeline}>{timeline}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="referenceImages" className="block text-gray-300 mb-2">
                  Reference Images (Optional)
                </label>
                <input
                  type="file"
                  id="referenceImages"
                  name="referenceImages"
                  onChange={handleChange}
                  multiple
                  accept="image/*"
                  className="w-full px-4 py-2 border border-gray-700 bg-gray-800 text-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-sm text-gray-400 mt-1">
                  You can upload up to 3 images for reference (max 5MB each).
                </p>
              </div>
              
              <div className="pt-4">
                <Button type="submit" variant="primary" size="lg">
                  Submit Custom Order Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold text-primary mb-8 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="border-b border-gray-700 pb-4">
              <h3 className="font-serif text-xl font-bold mb-2 text-light">How much does a custom hat cost?</h3>
              <p className="text-gray-400">
                Custom hat prices vary depending on the complexity of the design, materials used, and time required. Basic custom hats typically start from around Rp 1,200,000, while more intricate designs can range from Rp 2,500,000 to Rp 5,000,000+. We'll provide a detailed quote after reviewing your request.
              </p>
            </div>
            
            <div className="border-b border-gray-700 pb-4">
              <h3 className="font-serif text-xl font-bold mb-2 text-light">How long does it take to make a custom hat?</h3>
              <p className="text-gray-400">
                The production time varies based on the complexity of your design and our current workload. Simple designs typically take 1-2 weeks, while more complex hats may require 3-4 weeks. During peak seasons, production times may be longer.
              </p>
            </div>
            
            <div className="border-b border-gray-700 pb-4">
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Can I request changes to my design?</h3>
              <p className="text-gray-400">
                Yes! After submitting your request, we'll create a design concept for your approval. You can request up to two rounds of revisions before production begins. Once production starts, major changes may incur additional costs.
              </p>
            </div>
            
            <div className="border-b border-gray-700 pb-4">
              <h3 className="font-serif text-xl font-bold mb-2 text-light">Do you ship custom hats internationally?</h3>
              <p className="text-gray-400">
                Yes, we ship custom orders worldwide. International shipping costs and delivery times vary by location. We'll provide shipping details and tracking information once your hat is ready to ship.
              </p>
            </div>
            
            <div>
              <h3 className="font-serif text-xl font-bold mb-2 text-light">What if I'm not satisfied with my custom hat?</h3>
              <p className="text-gray-400">
                Customer satisfaction is our priority. If your hat doesn't meet the agreed-upon specifications, we'll work with you to address any issues. However, as custom items are made specifically for you, they are not eligible for returns unless there's a manufacturing defect.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomOrder;
